import React, { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Section } from '../components/ui/Section';
import { Reveal } from '../components/ui/Reveal';
import { Button } from '../components/ui/Button';
import { getPost } from '../data/posts';
export function BlogPost() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const post = slug ? getPost(slug) : undefined;
  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  useEffect(() => {
    window.scrollTo(0, 0);
    if (!post) return;
    document.title = post.metaTitle;
    let meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', post.metaDescription);
    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.setAttribute('data-post-schema', post.slug);
    ld.textContent = JSON.stringify([{
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: post.title,
      description: post.metaDescription,
      author: {
        '@type': 'Organization',
        name: 'Yetu Design Lab'
      },
      publisher: {
        '@type': 'Organization',
        name: 'Yetu Design Lab'
      },
      keywords: [post.keyphrase, ...post.keywords].join(', ')
    }, {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: post.faqs.map(f => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: f.answer
        }
      }))
    }]);
    document.head.appendChild(ld);
    return () => {
      ld.remove();
    };
  }, [post]);
  if (!post) return <Navigate to="/blog" replace data-id="element-305" data-mp="28jp0ksgmez0i" />;
  const related = post.related.map(s => getPost(s)).filter((p): p is NonNullable<typeof p> => Boolean(p));
  return <Layout data-id="element-306" data-mp="2dfsw0rcfjfgl">
      <Section aria-labelledby="post-title" data-id="element-307" data-mp="2df9mnz0uhze6">
        <Reveal onMount data-id="element-308" data-mp="2dgb20fow981p">
          <Link to="/blog" className="link t-label text-mut" data-id="element-309" data-mp="28gg61xq9hs9s">
            ← All articles
          </Link>
          <p className="t-label mt-10 text-subtle" data-id="element-310" data-mp="23hlqr5ri6luf">
            <span className="text-accent" data-id="element-311" data-mp="23gtsfn6iua0g">{post.category}</span> · {post.author}{' '}
            · {post.date} · {post.readTime}
          </p>
          <h1 id="post-title" className="t-h1 mt-6 max-w-[20ch] text-balance" data-id="element-312" data-mp="1up1lvnkmbl15">
            {post.title}
          </h1>
          <p className="t-lead measure mt-8 text-mut" data-id="element-313" data-mp="1ppf7vbwyu0ra">{post.intro}</p>
        </Reveal>
      </Section>

      <Section className="!pt-0" data-id="element-314" data-mp="1krn8xxxwledl">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-20" data-id="element-315" data-mp="1ksf1t1048y06">
          <article className="measure space-y-12" data-id="element-316" data-mp="1fr9btaxubouc">
            {post.sections.map((section, i) => <section key={i} className="space-y-5" data-id="element-317" data-mp="1bqpbjyhz053z" data-mpi={i}>
                {section.heading && <h2 className="t-h3 text-ink" data-id="element-318" data-mp="3nczl42o03yah">{section.heading}</h2>}
                {section.paragraphs.map((p, j) => <p key={j} className="t-body text-mut" data-id="element-319" data-mp="8th7f8necaj" data-mpi={j}>
                    {p}
                  </p>)}
                {section.bullets && <ul className="space-y-3 border-l border-line pl-6" data-id="element-320" data-mp="574szvii6wve">
                    {section.bullets.map((b, k) => <li key={k} className="t-body text-mut" data-id="element-321" data-mp="33npa31z7a1ip" data-mpi={k}>
                        {b.term && <span className="text-ink" data-id="element-322" data-mp="38owdkoub8ejk">{b.term}: </span>}
                        {b.text}
                      </li>)}
                  </ul>}
              </section>)}

            <section className="space-y-5 border-t border-line pt-10" data-id="element-323" data-mp="2kz248h979842">
              <h2 className="t-h3 text-ink" data-id="element-324" data-mp="2kysd2qcf4uws">Keep going</h2>
              <p className="t-body text-mut" data-id="element-325" data-mp="2pxdc4mfxp8jn">
                Explore how we put this into practice in our{' '}
                <Link to="/#work" className="link link--static text-ink" data-id="element-326" data-mp="viycxq2ucdzg">
                  selected work
                </Link>
                {post.caseStudy ? <>
                    {' '}
                    — or read the{' '}
                    <Link to={post.caseStudy.to} className="link link--static text-ink" data-id="element-327" data-mp="10h00oob7p1h9">
                      {post.caseStudy.label.replace('See the ', '')}
                    </Link>
                  </> : <>
                    {' '}
                    — or see{' '}
                    <Link to={post.serviceAnchor} className="link link--static text-ink" data-id="element-328" data-mp="1o4husqhy8bne">
                      how the studio works
                    </Link>
                  </>}
                .
              </p>
            </section>
          </article>

          <aside className="hidden lg:block" aria-label="Free consultation" data-id="element-329" data-mp="20gbjkg8m6kwg">
            <div className="panel sticky top-28 p-7" data-id="element-330" data-mp="20j66qfj0ijtk">
              <p className="t-label text-subtle" data-id="element-331" data-mp="25elhgylq3zlr">Free consultation</p>
              <h2 className="t-h3 mt-4" data-id="element-332" data-mp="1rp1je6337qis">
                Want this working for your business?
              </h2>
              <p className="t-small mt-3 text-mut" data-id="element-333" data-mp="1vp1vvl6mlrn7">
                We'll map the fastest path to the growth you're after — brand,
                web, or both.
              </p>
              <div className="mt-6" data-id="element-334" data-mp="1mpyc0ezmx87n">
                <Button variant="outline" fullWidth onClick={scrollToContact} data-id="element-335" data-mp="1mq872gzje5k7">
                  Book a free consultation
                </Button>
              </div>
            </div>
          </aside>
        </div>
      </Section>

      {related.length > 0 && <Section ruled aria-labelledby="related-title" data-id="element-336" data-mp="3335bt7u116lq">
          <h2 id="related-title" className="t-label mb-8 text-subtle" data-id="element-337" data-mp="331kg3evz7hma">
            Related articles
          </h2>
          <ul className="border-b border-line" data-id="element-338" data-mp="372xmbq2zdmtq">
            {related.map(r => <Reveal as="li" key={r.slug} className="border-t border-line" data-id="element-339" data-mp="juq490c4homb">
                <Link to={`/blog/${r.slug}`} className="group -mx-4 grid grid-cols-1 gap-3 px-4 py-7 transition-colors hover:bg-surface md:grid-cols-12 md:items-baseline md:gap-8" data-id="element-340" data-mp="ew6hwytmaatf">
                  <p className="t-label text-subtle md:col-span-3" data-id="element-341" data-mp="5zebuzlfwa8k">
                    <span className="text-accent" data-id="element-342" data-mp="12msrwvn6m562">{r.category}</span> ·{' '}
                    {r.readTime}
                  </p>
                  <h3 className="t-h3 transition-colors group-hover:text-accent md:col-span-9" data-id="element-343" data-mp="yo36qgvwjjbv">
                    {r.title}
                  </h3>
                </Link>
              </Reveal>)}
          </ul>
        </Section>}
    </Layout>;
}